import { useState, useEffect, useCallback, useRef, useMemo } from "react";

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DESIGN TOKENS — Military-grade dark theme, outdoor WCAG AAA contrast
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const T = {
    bg0: "#010b12", bg1: "#030f1a", bg2: "#051525", bg3: "#081e33", bg4: "#0c2440", bg5: "#102a4a",
    a: "#00e5a0", a2: "rgba(0,229,160,0.18)", a3: "rgba(0,229,160,0.08)", a4: "rgba(0,229,160,0.35)",
    cy: "#00d4ff", cy2: "rgba(0,212,255,0.15)",
    am: "#ffb300", am2: "rgba(255,179,0,0.15)",
    rd: "#ff2d55", rd2: "rgba(255,45,85,0.18)",
    gr: "#00e676", gr2: "rgba(0,230,118,0.15)",
    pu: "#d05af2", pu2: "rgba(208,90,242,0.15)",
    or: "#ff6d00", or2: "rgba(255,109,0,0.15)",
    ye: "#ffd740", ye2: "rgba(255,215,64,0.15)",
    tx: "#c8dde8", txL: "#8fb0c4", txD: "#4a7a95", txX: "#264558",
    br: "rgba(0,229,160,0.12)", brA: "rgba(0,229,160,0.30)", brX: "rgba(0,229,160,0.06)",
    shadow: "0 4px 32px rgba(0,0,0,0.7)",
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ROBOT STATES
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const STATES = {
    sowing: { color: T.gr, label: "Sowing", icon: "⬤", dot: 3.1 },
    spraying: { color: T.cy, label: "Spraying", icon: "◆", dot: 3.1 },
    cutting: { color: T.ye, label: "Cutting", icon: "▲", dot: 3.1 },
    returning: { color: T.or, label: "Returning", icon: "◀", dot: 2.8 },
    charging: { color: T.pu, label: "Charging", icon: "⚡", dot: 2.6 },
    idle: { color: T.txX, label: "Idle", icon: "●", dot: 2.2 },
    error: { color: T.rd, label: "Error", icon: "✕", dot: 3.5 },
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// UTILITIES
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const rnd = (a, b) => Math.random() * (b - a) + a;
const rndI = (a, b) => Math.floor(rnd(a, b));
const pf = (n, d = 1) => parseFloat(n.toFixed(d));
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const ts = () => new Date().toTimeString().slice(0, 8);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// COMPOSITE HEALTH SCORE ENGINE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function computeHealth(r) {
    const power = Math.round((r.cb * 0.4 + r.mb * 0.6));
    const mech = r.gc < 3 ? 20 : r.gc < 5 ? 55 : r.gc < 10 ? 90 : 100;
    const conn = [0, 25, 50, 75, 100][clamp(r.sig, 0, 4)];
    const phScore = r.sp >= 6.0 && r.sp <= 7.5 ? 100 : r.sp >= 5.5 && r.sp <= 8.0 ? 70 : 35;
    const mission = r.errors === 0 ? 100 : r.errors === 1 ? 72 : r.errors === 2 ? 45 : 20;
    const score = Math.round(power * 0.30 + mech * 0.20 + conn * 0.20 + phScore * 0.15 + mission * 0.15);
    return { score, power, mech, conn, phScore, mission };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// PREDICTIVE BATTERY ENGINE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function predictBattery(r) {
    const drainRate = r.state === "idle" ? 0.03 : r.state === "charging" ? -0.8 : 0.11;
    const minsTo15CB = r.cb > 15 ? Math.round((r.cb - 15) / drainRate) : 0;
    const minsTo15MB = r.mb > 15 ? Math.round((r.mb - 15) / drainRate) : 0;
    const criticalIn = Math.min(minsTo15CB, minsTo15MB);
    return { criticalIn, drainRate, minsTo15CB, minsTo15MB };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// WEATHER RULES ENGINE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function evaluateWeatherRules(weather) {
    const rules = [];
    if (weather.wind > 25) rules.push({ sev: "critical", action: "SUSPEND_SPRAY", msg: `Wind ${weather.wind.toFixed(1)} km/h — All spraying suspended`, icon: "💨" });
    if (weather.wind > 15 && weather.wind <= 25) rules.push({ sev: "warning", action: "REDUCE_SPRAY", msg: `Wind ${weather.wind.toFixed(1)} km/h — Spray accuracy reduced`, icon: "💨" });
    if (weather.rain > 2) rules.push({ sev: "critical", action: "SUSPEND_SOWING", msg: `Rain ${weather.rain.toFixed(1)} mm — Sowing suspended`, icon: "🌧️" });
    if (weather.temp > 36) rules.push({ sev: "warning", action: "REDUCE_SPEED", msg: `Temp ${weather.temp.toFixed(1)}°C — Speed reduced 30%`, icon: "🌡️" });
    if (weather.hum < 25) rules.push({ sev: "warning", action: "DUST_RISK", msg: `Humidity ${weather.hum}% — Dust contamination risk`, icon: "⚠️" });
    return rules;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ALERT FATIGUE MANAGER — groups similar alerts
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function groupAlerts(alerts) {
    const groups = {};
    alerts.forEach(a => {
        const key = a.group || a.id;
        if (!groups[key]) groups[key] = { ...a, count: 1 };
        else groups[key].count++;
    });
    return Object.values(groups);
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// INITIAL DATA
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const statePool = "sowing,sowing,sowing,sowing,sowing,sowing,spraying,spraying,spraying,spraying,cutting,cutting,cutting,idle,idle,idle,returning,error,error,charging".split(",");

function makeRobots() {
    return Array.from({ length: 100 }, (_, i) => {
        const zone = "ABCD"[Math.floor(i / 25)];
        const state = statePool[rndI(0, statePool.length)];
        return {
            id: i + 1,
            fid: `R${String(i + 1).padStart(3, "0")}`,
            state,
            x: (i % 10) * 9.6 + rnd(1.8, 3.5),
            y: Math.floor(i / 10) * 9.6 + rnd(1.8, 3.5),
            cb: rndI(20, 100), mb: rndI(15, 100),
            spd: state === "idle" || state === "charging" ? 0 : rndI(4, 18),
            gc: pf(rnd(3, 13)), sp: pf(rnd(5.6, 8.2)),
            sig: rndI(2, 5), upd: Date.now() - rndI(0, 15000),
            dir: ["N", "NE", "E", "SE", "S", "SW", "W", "NW"][rndI(0, 8)],
            mission: ["Zone-" + zone + " Sowing", "Zone-" + zone + " Spraying", "Zone-" + zone + " Cutting", "Return to Base", "Standby", "Zone-" + zone + " Soil Scan"][rndI(0, 6)],
            zone, fw: `v2.${rndI(1, 9)}.${rndI(0, 9)}`,
            uptime: rndI(1, 96), errors: rndI(0, 3),
            cbHistory: Array.from({ length: 20 }, () => rndI(60, 100)),
            speedHistory: Array.from({ length: 20 }, () => rndI(0, 18)),
            organicRisk: Math.random() < 0.05,
        };
    });
}

const INIT_ALERTS = [
    { id: 1, lvl: "critical", msg: "R042 — Motor battery critical (8%)", t: "12:41:03", rid: 42, group: "bat_critical", ack: false },
    { id: 2, lvl: "warning", msg: "R017 — Soil pH anomaly detected (4.1)", t: "12:40:51", rid: 17, group: "soil_warn", ack: false },
    { id: 3, lvl: "critical", msg: "R085 — Ground clearance fault (<2cm)", t: "12:40:22", rid: 85, group: "gc_critical", ack: false },
    { id: 4, lvl: "critical", msg: "R003 — ORGANIC BOUNDARY BREACH detected!", t: "12:39:58", rid: 3, group: "organic", ack: false },
    { id: 5, lvl: "info", msg: "R033 — Zone-B spray mission complete", t: "12:39:45", rid: 33, group: "complete", ack: false },
    { id: 6, lvl: "warning", msg: "R061 — MQTT signal degraded (1/4 bars)", t: "12:39:12", rid: 61, group: "sig_warn", ack: false },
    { id: 7, lvl: "warning", msg: "R094 — Wind >25 km/h — spraying paused", t: "12:38:30", rid: 94, group: "weather", ack: false },
    { id: 8, lvl: "info", msg: "R008 — Zone-A sowing mission initiated", t: "12:38:00", rid: 8, group: "start", ack: true },
    { id: 9, lvl: "warning", msg: "R055 — Controller battery at 19%", t: "12:37:30", rid: 55, group: "bat_warn", ack: false },
    { id: 10, lvl: "info", msg: "R071 — Zone-D sowing initiated", t: "12:37:00", rid: 71, group: "start", ack: true },
    { id: 11, lvl: "critical", msg: "R019 — Predictive: Battery critical in 8min", t: "12:36:44", rid: 19, group: "bat_predict", ack: false },
    { id: 12, lvl: "warning", msg: "R047 — High temp zone detected — speed capped", t: "12:36:10", rid: 47, group: "temp", ack: false },
];

const INIT_TIMELINE = [
    { id: 1, ev: "Zone-A batch mission launched — 25 robots active", type: "start", t: "12:30:00" },
    { id: 2, ev: "Weather engine: Wind spike 28km/h — spray protocol triggered", type: "weather", t: "12:31:15" },
    { id: 3, ev: "R085 ground clearance fault — auto-stopped, zone flagged", type: "error", t: "12:32:40" },
    { id: 4, ev: "Organic boundary alert — R003 within 2m buffer, speed cut", type: "organic", t: "12:33:55" },
    { id: 5, ev: "Batch command: 12 robots redirected to Zone-C cutting", type: "batch", t: "12:35:20" },
    { id: 6, ev: "Emergency STOP triggered by operator — wind alert, cleared", type: "estop", t: "12:36:00" },
    { id: 7, ev: "Zone-B spray mission completed — coverage 98.4%", type: "complete", t: "12:39:45" },
    { id: 8, ev: "Predictive alert: R042 battery critical in <10min", type: "predict", t: "12:41:00" },
    { id: 9, ev: "Fleet health check — 96/100 robots operational", type: "info", t: "12:25:00" },
];

const AUDIT_LOG = [
    { id: 1, op: "ESTOP", by: "Operator-01", t: "12:36:00", detail: "Wind emergency — 100 robots halted" },
    { id: 2, op: "BATCH", by: "Operator-01", t: "12:35:20", detail: "12 robots → Zone-C cutting" },
    { id: 3, op: "SINGLE", by: "System-AUTO", t: "12:32:40", detail: "R085 auto-stopped — GC fault" },
    { id: 4, op: "RESUME", by: "Operator-02", t: "12:36:45", detail: "Fleet resumed after ESTOP clearance" },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SUB-COMPONENTS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function BatBar({ pct, color, label, predictMin }) {
    const c = pct < 15 ? T.rd : pct < 30 ? T.am : color;
    const warn = predictMin && predictMin < 20;
    return (
        <div style={{ marginBottom: 9 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                <span style={{ color: T.txD, fontSize: 10 }}>{label}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {warn && <span style={{ color: T.am, fontSize: 9, animation: "blink 1s infinite" }}>~{predictMin}min</span>}
                    <span style={{ color: c, fontWeight: 700, fontSize: 11 }}>{Math.round(pct)}%</span>
                </div>
            </div>
            <div style={{ height: 6, background: T.bg4, borderRadius: 3, overflow: "hidden", position: "relative" }}>
                <div style={{
                    width: `${Math.max(0, pct)}%`, height: "100%", background: c, borderRadius: 3,
                    transition: "width 1.2s cubic-bezier(0.4,0,0.2,1)", boxShadow: `0 0 10px ${c}60`
                }} />
                {pct < 25 && <div style={{ position: "absolute", inset: 0, background: `linear-gradient(90deg,transparent,${c}22)`, animation: "shimmer 1.5s infinite" }} />}
            </div>
        </div>
    );
}

function HealthRing({ score, size = 52 }) {
    const c = score >= 75 ? T.gr : score >= 50 ? T.am : T.rd;
    const r = (size / 2) - 5;
    const circ = 2 * Math.PI * r;
    const dash = (score / 100) * circ;
    return (
        <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
            <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={T.bg4} strokeWidth="4" />
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={c} strokeWidth="4"
                    strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
                    style={{ transition: "stroke-dasharray 1.5s cubic-bezier(0.4,0,0.2,1)", filter: `drop-shadow(0 0 4px ${c})` }} />
            </svg>
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: c, fontSize: size > 48 ? 13 : 10, fontWeight: 700, lineHeight: 1 }}>{score}</span>
                <span style={{ color: T.txD, fontSize: 7, lineHeight: 1, marginTop: 1 }}>HEALTH</span>
            </div>
        </div>
    );
}

function MiniSparkline({ data, color, width = 80, height = 24 }) {
    if (!data || data.length < 2) return null;
    const max = Math.max(...data), min = Math.min(...data);
    const range = max - min || 1;
    const pts = data.map((v, i) => {
        const x = (i / (data.length - 1)) * width;
        const y = height - ((v - min) / range) * (height - 4) - 2;
        return `${x},${y}`;
    }).join(" ");
    return (
        <svg width={width} height={height} style={{ overflow: "visible" }}>
            <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ filter: `drop-shadow(0 0 3px ${color}80)` }} />
        </svg>
    );
}

function SigBars({ sig }) {
    return (
        <div style={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
            {[1, 2, 3, 4].map(i => (
                <div key={i} style={{ width: 3, height: i * 3 + 3, background: i <= sig ? (i <= 1 ? T.rd : i <= 2 ? T.am : T.a) : T.bg4, borderRadius: 1, transition: "background 0.4s" }} />
            ))}
        </div>
    );
}

function StatPill({ label, val, color, blink }) {
    return (
        <div style={{
            background: T.bg3, borderRadius: 5, padding: "5px 9px", textAlign: "center",
            border: `1px solid ${color}22`, minWidth: 40, flexShrink: 0
        }}>
            <div style={{ color, fontSize: 15, fontWeight: 700, lineHeight: 1, animation: blink ? "blink 1s infinite" : undefined }}>{val}</div>
            <div style={{ color: T.txD, fontSize: 7, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
        </div>
    );
}

function Divider() {
    return <div style={{ width: 1, height: 28, background: T.br, flexShrink: 0 }} />;
}

function SectionLabel({ text }) {
    return <div style={{ color: T.txD, fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", borderBottom: `1px solid ${T.bg4}`, paddingBottom: 5, marginBottom: 8, marginTop: 2 }}>{text}</div>;
}

function DataRow({ label, val, color, sub }) {
    return (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", borderBottom: `1px solid ${T.bg4}` }}>
            <span style={{ color: T.txD, fontSize: 10 }}>{label}</span>
            <div style={{ textAlign: "right" }}>
                <span style={{ color: color || T.tx, fontSize: 11, fontWeight: 600 }}>{val}</span>
                {sub && <div style={{ color: T.txD, fontSize: 8, marginTop: 1 }}>{sub}</div>}
            </div>
        </div>
    );
}

const ALC = { critical: T.rd, warning: T.am, info: T.cy, organic: T.pu, predict: T.or };
const TLC = { start: T.a, complete: T.cy, weather: T.am, error: T.rd, batch: T.cy, estop: T.rd, organic: T.pu, predict: T.or, info: T.txD };

function Sec({ label, children }) { return (<div style={{ marginBottom: 12 }}><SectionLabel text={label} />{children}</div>); }
//function SectionLabel({ text }) { return (<div style={{ color: T.txD, fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", borderBottom: `1px solid ${T.bg4}`, paddingBottom: 5, marginBottom: 8 }}>{text}</div>); }
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN COMMAND CENTER
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function AgriCommandCenter() {
    const [robots, setRobots] = useState(makeRobots);
    const [alerts, setAlerts] = useState(INIT_ALERTS);
    const [timeline] = useState(INIT_TIMELINE);
    const [audit] = useState(AUDIT_LOG);
    const [sel, setSel] = useState(null);
    const [msel, setMsel] = useState(new Set());
    const [weather, setWeather] = useState({ wind: 12.4, rain: 0.0, temp: 24.2, hum: 58 });
    const [estop, setEstop] = useState(false);
    const [focus, setFocus] = useState(false);
    const [batch, setBatch] = useState(false);
    const [tick, setTick] = useState(0);
    const [now, setNow] = useState(new Date());
    const [leftTab, setLeftTab] = useState("alerts");
    const [rightTab, setRightTab] = useState("telemetry");
    const [filter, setFilter] = useState("all");
    const [mapZoom, setMapZoom] = useState(1);
    const [orgAlert, setOrgAlert] = useState(false);
    const estopRef = useRef(false);
    estopRef.current = estop;

    // ── Live simulation ──────────────────────────────────────────────────────
    useEffect(() => {
        const iv = setInterval(() => {
            setNow(new Date());
            setTick(t => t + 1);
            if (!estopRef.current) {
                setRobots(prev => prev.map(r => {
                    const newCb = clamp(r.cb - rnd(0, 0.06), 0, 100);
                    const newMb = clamp(r.mb - rnd(0, 0.10), 0, 100);
                    const newSpd = r.state === "idle" || r.state === "charging" ? 0 : clamp(r.spd + rnd(-0.6, 0.6), 0, 20);
                    const newSp = pf(clamp(r.sp + rnd(-0.03, 0.03), 4.0, 9.0));
                    const newGc = pf(clamp(r.gc + rnd(-0.04, 0.04), 1.0, 15.0));
                    const newUpd = Math.random() > 0.05 ? Date.now() : r.upd;
                    const newCbH = [...r.cbHistory.slice(1), Math.round(newCb)];
                    const newSpdH = [...r.speedHistory.slice(1), Math.round(newSpd)];
                    return { ...r, cb: newCb, mb: newMb, spd: newSpd, sp: newSp, gc: newGc, upd: newUpd, cbHistory: newCbH, speedHistory: newSpdH };
                }));
                setWeather(prev => ({
                    ...prev,
                    wind: pf(clamp(prev.wind + rnd(-0.3, 0.3), 0, 45)),
                    temp: pf(clamp(prev.temp + rnd(-0.08, 0.08), 10, 42)),
                    rain: pf(clamp(prev.rain + rnd(-0.02, 0.04), 0, 15)),
                }));
            } else {
                setRobots(prev => prev.map(r => ({ ...r, spd: 0, state: r.state === "error" ? "error" : "idle" })));
            }
        }, 1000);
        return () => clearInterval(iv);
    }, []);

    // ── Weather rules engine ─────────────────────────────────────────────────
    const weatherRules = useMemo(() => evaluateWeatherRules(weather), [Math.round(weather.wind), Math.round(weather.rain * 10), Math.round(weather.temp)]);

    // ── Fleet statistics ─────────────────────────────────────────────────────
    const stats = useMemo(() => ({
        active: robots.filter(r => ["sowing", "spraying", "cutting", "returning"].includes(r.state)).length,
        err: robots.filter(r => r.state === "error").length,
        sow: robots.filter(r => r.state === "sowing").length,
        spr: robots.filter(r => r.state === "spraying").length,
        cut: robots.filter(r => r.state === "cutting").length,
        idle: robots.filter(r => r.state === "idle").length,
        chg: robots.filter(r => r.state === "charging").length,
        lowBat: robots.filter(r => r.cb < 20 || r.mb < 20).length,
        critical: alerts.filter(a => a.lvl === "critical" && !a.ack).length,
        organic: alerts.filter(a => a.group === "organic").length,
        avgHealth: Math.round(robots.reduce((s, r) => s + computeHealth(r).score, 0) / 100),
        connected: robots.filter(r => Date.now() - r.upd < 30000).length,
    }), [robots, alerts]);

    const flash = tick % 2 === 0;
    const robot = robots.find(r => r.id === sel);

    // ── Actions ──────────────────────────────────────────────────────────────
    const doEstop = useCallback(() => {
        if (estop) return;
        setEstop(true);
        setAlerts(prev => [{ id: Date.now(), lvl: "critical", msg: "🛑 EMERGENCY STOP — All 100 robots halted by operator", t: ts(), rid: null, group: "estop", ack: false }, ...prev.slice(0, 29)]);
        setTimeout(() => setEstop(false), 10000);
    }, [estop]);

    const doRobotClick = useCallback((r) => {
        if (batch) {
            setMsel(prev => { const n = new Set(prev); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; });
        } else {
            setSel(prev => prev === r.id ? null : r.id);
            setFocus(true);
        }
    }, [batch]);

    const doBatch = useCallback((action) => {
        const count = msel.size;
        setAlerts(prev => [{ id: Date.now(), lvl: "info", msg: `Batch "${action}" dispatched → ${count} robots`, t: ts(), rid: null, group: "batch", ack: false }, ...prev]);
        setMsel(new Set()); setBatch(false);
    }, [msel]);

    const doAck = useCallback((id) => {
        setAlerts(prev => prev.map(a => a.id === id ? { ...a, ack: true } : a));
    }, []);

    const filteredAlerts = useMemo(() => {
        const base = filter === "all" ? alerts : alerts.filter(a => a.lvl === filter || a.group === filter);
        return groupAlerts(base);
    }, [alerts, filter]);

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // RENDER
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    return (
        <div style={{
            background: T.bg0, color: T.tx, fontFamily: '"IBM Plex Mono","Fira Mono","Courier New",monospace',
            height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden", fontSize: "12px", userSelect: "none"
        }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box}
        ::-webkit-scrollbar{width:3px;height:3px}
        ::-webkit-scrollbar-track{background:${T.bg1}}
        ::-webkit-scrollbar-thumb{background:${T.bg5};border-radius:2px}
        @keyframes blink{0%,100%{opacity:1}50%{opacity:0.2}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        @keyframes shimmer{0%{transform:translateX(-100%)}100%{transform:translateX(400%)}}
        @keyframes errRing{0%{r:4;opacity:0.9}80%{r:8;opacity:0}100%{r:8;opacity:0}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}
        .rb:hover{background:${T.bg3}!important;border-color:${T.a}!important;color:${T.a}!important}
        .mapDot:hover circle{filter:brightness(1.4)}
      `}</style>

            {/* ╔═══════════════════════════════════════════════════════════╗ */}
            {/* ║                     TOP COMMAND BAR                       ║ */}
            {/* ╚═══════════════════════════════════════════════════════════╝ */}
            <div style={{
                display: "flex", alignItems: "center", gap: 8, padding: "7px 12px",
                background: T.bg1, borderBottom: `1px solid ${T.br}`, flexShrink: 0, minHeight: 54
            }}>
                {/* Brand */}
                <div style={{ flexShrink: 0 }}>
                    <div style={{
                        color: T.a, fontSize: 13, fontWeight: 700, letterSpacing: "0.16em", lineHeight: 1,
                        textShadow: `0 0 20px ${T.a}60`
                    }}>AGRI-OPS</div>
                    <div style={{ color: T.txD, fontSize: 7.5, letterSpacing: "0.18em", marginTop: 1 }}>
                        MULTI-ROBOT COMMAND CENTER v3.1
                    </div>
                </div>
                <Divider />

                {/* Fleet stats */}
                <StatPill label="Fleet" val={100} color={T.a} />
                <StatPill label="Active" val={stats.active} color={T.cy} />
                <StatPill label="Sowing" val={stats.sow} color={T.gr} />
                <StatPill label="Spray" val={stats.spr} color={T.cy} />
                <StatPill label="Cutting" val={stats.cut} color={T.ye} />
                <StatPill label="Errors" val={stats.err} color={T.rd} blink={stats.err > 0} />
                <StatPill label="Low Bat" val={stats.lowBat} color={T.am} blink={stats.lowBat > 3} />
                <StatPill label="Alerts" val={stats.critical} color={T.rd} blink={stats.critical > 0} />
                <StatPill label="Avg Hlth" val={`${stats.avgHealth}`} color={stats.avgHealth >= 75 ? T.gr : stats.avgHealth >= 50 ? T.am : T.rd} />

                <Divider />

                {/* Weather strip */}
                <div style={{
                    display: "flex", gap: 10, alignItems: "center", padding: "4px 10px",
                    background: T.bg3, borderRadius: 5, border: `1px solid ${T.br}`,
                    fontSize: 9.5, color: T.txL, flexShrink: 0
                }}>
                    {weatherRules.length > 0 && (
                        <span style={{ color: T.am, fontWeight: 700, animation: "blink 1.5s infinite" }}>
                            {weatherRules[0].icon} {weatherRules[0].msg.slice(0, 28)}
                        </span>
                    )}
                    {weatherRules.length === 0 && <span style={{ color: T.a }}>✓ WEATHER CLEAR</span>}
                    <span style={{ color: T.txD }}>|</span>
                    <span>WIND {weather.wind.toFixed(1)} km/h</span>
                    <span>TEMP {weather.temp.toFixed(1)}°C</span>
                    <span>HUM {weather.hum}%</span>
                    <span>RAIN {weather.rain.toFixed(1)} mm</span>
                </div>

                {/* Connectivity pulse */}
                <div style={{
                    display: "flex", alignItems: "center", gap: 5, padding: "4px 9px",
                    background: T.bg3, borderRadius: 5, border: `1px solid ${T.br}`, fontSize: 9.5, flexShrink: 0
                }}>
                    <div style={{
                        width: 7, height: 7, borderRadius: "50%", background: T.a,
                        boxShadow: `0 0 8px ${T.a}`, animation: "pulse 2s infinite"
                    }} />
                    <span style={{ color: T.txL }}>MQTT LIVE</span>
                    <span style={{ color: T.txD }}>|</span>
                    <span style={{ color: T.a }}>{stats.connected}/100</span>
                    <span style={{ color: T.txD }}>48ms</span>
                </div>

                <div style={{ flex: 1 }} />

                {/* Clock */}
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ color: T.tx, fontSize: 14, fontWeight: 700, letterSpacing: "0.05em", lineHeight: 1 }}>
                        {now.toTimeString().slice(0, 8)}
                    </div>
                    <div style={{ color: T.txD, fontSize: 8, marginTop: 1 }}>{now.toLocaleDateString()}</div>
                </div>

                {/* EMERGENCY STOP */}
                <button onClick={doEstop} style={{
                    background: estop ? T.rd : "rgba(255,45,85,0.10)",
                    border: `2px solid ${T.rd}`, color: estop ? "#fff" : T.rd,
                    borderRadius: 6, padding: "9px 18px", cursor: "pointer",
                    fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", flexShrink: 0,
                    boxShadow: estop ? `0 0 30px ${T.rd}90, 0 0 60px ${T.rd}30` : `0 0 12px ${T.rd}25`,
                    animation: estop ? "blink 0.5s infinite" : undefined,
                    transition: "all 0.2s", textShadow: estop ? "none" : undefined,
                }}>
                    {estop ? "STOPPED" : "⬛ STOP ALL"}
                </button>
            </div>

            {/* E-STOP Banner */}
            {estop && (
                <div style={{
                    background: "rgba(255,45,85,0.12)", borderBottom: `1px solid ${T.rd}`,
                    padding: "5px 14px", textAlign: "center", color: T.rd, fontSize: 10.5,
                    fontWeight: 700, letterSpacing: "0.2em", animation: "pulse 0.7s infinite",
                    flexShrink: 0, textShadow: `0 0 20px ${T.rd}`
                }}>
                    ⬛ EMERGENCY STOP ACTIVE — ALL 100 ROBOTS HALTING — ISO 13849 SAFE SHUTDOWN IN PROGRESS — AUTO-CLEAR IN 10s
                </div>
            )}

            {/* Weather Rules Banners */}
            {weatherRules.map((r, i) => (
                <div key={i} style={{
                    background: `rgba(255,179,0,0.09)`, borderBottom: `1px solid ${T.am}50`,
                    padding: "3px 14px", display: "flex", alignItems: "center", gap: 10,
                    flexShrink: 0, fontSize: 9.5
                }}>
                    <span style={{ color: T.am, fontWeight: 700 }}>{r.icon} WEATHER RULE ACTIVE</span>
                    <span style={{ color: T.am }}>{r.msg}</span>
                    <span style={{ color: T.txD }}>Action: <span style={{ color: T.cy }}>{r.action}</span></span>
                </div>
            ))}

            {/* ╔═══════════════════════════════════════════════════════════╗ */}
            {/* ║                    MAIN CONTENT AREA                      ║ */}
            {/* ╚═══════════════════════════════════════════════════════════╝ */}
            <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

                {/* ═══ LEFT PANEL: Alerts + Timeline + Audit ════════════════ */}
                <div style={{
                    width: 272, display: "flex", flexDirection: "column", borderRight: `1px solid ${T.br}`,
                    overflow: "hidden", flexShrink: 0, background: T.bg1
                }}>

                    {/* Tab row */}
                    <div style={{ display: "flex", borderBottom: `1px solid ${T.br}`, flexShrink: 0 }}>
                        {[
                            { k: "alerts", label: `Alerts ${stats.critical > 0 ? `(${stats.critical})` : ""}` },
                            { k: "timeline", label: "Timeline" },
                            { k: "audit", label: "Audit" },
                        ].map(tb => (
                            <button key={tb.k} onClick={() => setLeftTab(tb.k)} style={{
                                flex: 1, background: leftTab === tb.k ? T.bg2 : "transparent", border: "none",
                                borderBottom: `2px solid ${leftTab === tb.k ? T.a : "transparent"}`,
                                color: leftTab === tb.k ? T.a : T.txD, padding: "7px 0",
                                cursor: "pointer", fontSize: 9.5, letterSpacing: "0.08em",
                                textTransform: "uppercase", transition: "all 0.15s",
                            }}>{tb.label}</button>
                        ))}
                    </div>

                    {/* ALERTS TAB */}
                    {leftTab === "alerts" && (
                        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", flex: 1 }}>
                            {/* Filter chips */}
                            <div style={{ padding: "5px 8px", display: "flex", gap: 4, borderBottom: `1px solid ${T.br}`, flexShrink: 0, flexWrap: "wrap" }}>
                                {["all", "critical", "warning", "info", "organic"].map(f => {
                                    const cnt = f === "all" ? alerts.length : alerts.filter(a => a.lvl === f || a.group === f).length;
                                    const c = { all: T.a, critical: T.rd, warning: T.am, info: T.cy, organic: T.pu }[f];
                                    return (
                                        <button key={f} onClick={() => setFilter(f)} style={{
                                            background: filter === f ? `${c}22` : "transparent",
                                            border: `1px solid ${filter === f ? c : T.brX}`, borderRadius: 3,
                                            color: filter === f ? c : T.txD, padding: "2px 8px",
                                            cursor: "pointer", fontSize: 8.5, transition: "all 0.15s", textTransform: "uppercase",
                                        }}>{cnt} {f}</button>
                                    );
                                })}
                            </div>

                            {/* Alert list */}
                            <div style={{ flex: 1, overflowY: "auto" }}>
                                {filteredAlerts.map(a => (
                                    <div key={a.id} onClick={() => a.rid && setSel(a.rid)}
                                        style={{
                                            display: "flex", gap: 0, padding: "7px 8px",
                                            borderBottom: `1px solid ${T.bg3}`,
                                            borderLeft: `3px solid ${ALC[a.lvl] || ALC[a.group] || T.txD}`,
                                            cursor: a.rid ? "pointer" : "default",
                                            opacity: a.ack ? 0.45 : 1,
                                            background: T.bg1, transition: "background 0.12s",
                                            animation: !a.ack && a.lvl === "critical" ? "fadeIn 0.3s ease" : undefined,
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = T.bg2}
                                        onMouseLeave={e => e.currentTarget.style.background = T.bg1}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 4 }}>
                                                <div style={{ color: T.tx, fontSize: 10, lineHeight: 1.5, flex: 1 }}>
                                                    {a.count > 1 && <span style={{ color: T.am, fontWeight: 700, marginRight: 4 }}>×{a.count}</span>}
                                                    {a.msg}
                                                </div>
                                                {!a.ack && (
                                                    <button onClick={e => { e.stopPropagation(); doAck(a.id); }} style={{
                                                        background: "transparent", border: `1px solid ${T.txX}`, borderRadius: 2,
                                                        color: T.txD, fontSize: 7.5, padding: "1px 4px", cursor: "pointer", flexShrink: 0,
                                                    }}>ACK</button>
                                                )}
                                            </div>
                                            <div style={{ color: T.txD, fontSize: 8, marginTop: 2 }}>{a.t} {a.rid && `· R${String(a.rid).padStart(3, "0")}`}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* TIMELINE TAB */}
                    {leftTab === "timeline" && (
                        <div style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
                            {timeline.map((item, i) => (
                                <div key={item.id} style={{ display: "flex", gap: 9, padding: "6px 10px" }}>
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 3 }}>
                                        <div style={{
                                            width: 8, height: 8, borderRadius: "50%", background: TLC[item.type] || T.txD, flexShrink: 0,
                                            boxShadow: `0 0 6px ${TLC[item.type] || T.txD}80`
                                        }} />
                                        {i < timeline.length - 1 && <div style={{ width: 1, flex: 1, background: T.bg4, marginTop: 3 }} />}
                                    </div>
                                    <div style={{ flex: 1, paddingBottom: 6 }}>
                                        <div style={{ color: T.tx, fontSize: 10, lineHeight: 1.5 }}>{item.ev}</div>
                                        <div style={{ color: T.txD, fontSize: 8, marginTop: 1 }}>{item.t}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* AUDIT TAB */}
                    {leftTab === "audit" && (
                        <div style={{ flex: 1, overflowY: "auto" }}>
                            <div style={{ padding: "6px 10px", color: T.txD, fontSize: 9, borderBottom: `1px solid ${T.br}` }}>
                                ISO 13849 Immutable Audit Log
                            </div>
                            {audit.map(a => (
                                <div key={a.id} style={{ padding: "8px 10px", borderBottom: `1px solid ${T.bg3}` }}>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span style={{ color: a.op === "ESTOP" ? T.rd : a.op === "BATCH" ? T.cy : T.a, fontSize: 10, fontWeight: 700 }}>{a.op}</span>
                                        <span style={{ color: T.txD, fontSize: 9 }}>{a.t}</span>
                                    </div>
                                    <div style={{ color: T.txL, fontSize: 9.5, marginTop: 2 }}>{a.detail}</div>
                                    <div style={{ color: T.txD, fontSize: 8, marginTop: 1 }}>by {a.by}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ═══ CENTER: SVG Field Map ═════════════════════════════════ */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: T.bg0 }}>

                    {/* Map toolbar */}
                    <div style={{
                        padding: "5px 10px", display: "flex", alignItems: "center", gap: 7,
                        borderBottom: `1px solid ${T.br}`, background: T.bg1, flexShrink: 0, flexWrap: "wrap"
                    }}>
                        <span style={{ color: T.a, fontSize: 9.5, fontWeight: 600, letterSpacing: "0.1em" }}>
                            FIELD MAP — 1000m × 1000m
                        </span>
                        <div style={{ color: T.txD, fontSize: 9 }}>|</div>
                        {/* Legend */}
                        {Object.entries(STATES).map(([k, v]) => (
                            <div key={k} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                                <div style={{
                                    width: 7, height: 7, borderRadius: "50%", background: v.color,
                                    boxShadow: `0 0 4px ${v.color}80`
                                }} />
                                <span style={{ color: T.txD, fontSize: 8.5 }}>{v.label}</span>
                            </div>
                        ))}
                        <div style={{ flex: 1 }} />

                        {/* Zoom */}
                        <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
                            {[0.7, 1, 1.4].map(z => (
                                <button key={z} onClick={() => setMapZoom(z)} style={{
                                    background: mapZoom === z ? T.a2 : "transparent",
                                    border: `1px solid ${mapZoom === z ? T.a : T.brX}`,
                                    color: mapZoom === z ? T.a : T.txD, borderRadius: 3,
                                    padding: "2px 7px", cursor: "pointer", fontSize: 9, transition: "all 0.15s",
                                }}>{z === 0.7 ? "−" : z === 1 ? "1:1" : "+"}</button>
                            ))}
                        </div>

                        <button onClick={() => { setFocus(f => !f); if (focus) setSel(null); }} style={{
                            background: focus ? T.a2 : "transparent", border: `1px solid ${focus ? T.a : T.brX}`,
                            color: focus ? T.a : T.txD, borderRadius: 3, padding: "3px 9px",
                            cursor: "pointer", fontSize: 9, transition: "all 0.15s",
                        }}>FOCUS {focus ? "ON" : "OFF"}</button>

                        <button onClick={() => { setBatch(b => !b); if (batch) setMsel(new Set()); }} style={{
                            background: batch ? T.cy2 : "transparent", border: `1px solid ${batch ? T.cy : T.brX}`,
                            color: batch ? T.cy : T.txD, borderRadius: 3, padding: "3px 9px",
                            cursor: "pointer", fontSize: 9, transition: "all 0.15s",
                        }}>MULTI-SEL {batch ? "ON" : "OFF"}</button>
                    </div>

                    {/* Batch action bar */}
                    {batch && msel.size > 0 && (
                        <div style={{
                            padding: "4px 10px", background: T.bg2, borderBottom: `1px solid ${T.br}`,
                            display: "flex", alignItems: "center", gap: 7, flexShrink: 0, flexWrap: "wrap",
                            animation: "fadeIn 0.2s ease"
                        }}>
                            <span style={{ color: T.cy, fontSize: 10, fontWeight: 700 }}>
                                {msel.size} robots selected
                            </span>
                            <div style={{ flex: 1 }} />
                            {["Return to Base", "Start Sowing", "Start Spraying", "Start Cutting", "Pause All", "Resume All", "Diagnostics"].map(a => (
                                <button key={a} onClick={() => doBatch(a)} style={{
                                    background: T.a3, border: `1px solid ${T.a}`, color: T.a,
                                    borderRadius: 3, padding: "3px 10px", cursor: "pointer", fontSize: 9, transition: "all 0.15s",
                                }} onMouseEnter={e => e.currentTarget.style.background = T.a2}
                                    onMouseLeave={e => e.currentTarget.style.background = T.a3}>{a}</button>
                            ))}
                            <button onClick={() => { setMsel(new Set()); setBatch(false); }} style={{
                                background: "transparent", border: `1px solid ${T.txX}`, color: T.txD,
                                borderRadius: 3, padding: "3px 8px", cursor: "pointer", fontSize: 9,
                            }}>CLEAR</button>
                        </div>
                    )}

                    {/* SVG Map */}
                    <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                        <svg viewBox={`0 0 ${100 / mapZoom} ${100 / mapZoom}`}
                            style={{ width: "100%", height: "100%", display: "block" }}
                            xmlns="http://www.w3.org/2000/svg">

                            {/* Background */}
                            <rect width="200" height="200" x="-50" y="-50" fill={T.bg0} />

                            {/* Crop grid lines */}
                            {Array.from({ length: 21 }, (_, i) => (
                                <line key={`h${i}`} x1="0" y1={i * 5} x2="100" y2={i * 5}
                                    stroke="rgba(0,60,20,0.28)" strokeWidth="0.18" />
                            ))}
                            {Array.from({ length: 21 }, (_, i) => (
                                <line key={`v${i}`} x1={i * 5} y1="0" x2={i * 5} y2="100"
                                    stroke="rgba(0,60,20,0.14)" strokeWidth="0.12" />
                            ))}

                            {/* Zone dividers */}
                            <line x1="50" y1="0" x2="50" y2="100" stroke={T.br} strokeWidth="0.5" strokeDasharray="3,2" />
                            <line x1="0" y1="50" x2="100" y2="50" stroke={T.br} strokeWidth="0.5" strokeDasharray="3,2" />

                            {/* Organic boundary (example Zone-A buffer) */}
                            <rect x="1" y="1" width="48" height="48" fill="none" stroke={T.pu} strokeWidth="0.35"
                                strokeDasharray="1.5,1.5" opacity="0.45" />
                            <text x="2" y="5" style={{ fill: T.pu, fontSize: "2px", fontFamily: "monospace", opacity: 0.6 }}>ORGANIC ZONE-A</text>

                            {/* Zone watermarks */}
                            {[["A", 25, 25], ["B", 75, 25], ["C", 25, 75], ["D", 75, 75]].map(([z, x, y]) => (
                                <text key={z} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
                                    style={{ fill: "rgba(0,229,160,0.04)", fontSize: "16px", fontWeight: "bold", fontFamily: "monospace" }}>{z}</text>
                            ))}
                            {[["ZONE A", 2, 3.8], ["ZONE B", 52, 3.8], ["ZONE C", 2, 53.8], ["ZONE D", 52, 53.8]].map(([z, x, y]) => (
                                <text key={z} x={x} y={y} style={{ fill: T.txD, fontSize: "2px", fontFamily: "monospace", letterSpacing: "0.12em" }}>{z}</text>
                            ))}

                            {/* Robots */}
                            {robots.map(r => {
                                const st = STATES[r.state] || STATES.idle;
                                const isSel = r.id === sel;
                                const isM = msel.has(r.id);
                                const isErr = r.state === "error";
                                const isOrg = r.organicRisk;
                                const op = focus && !batch ? (isSel ? 1 : 0.07) : 1;
                                const health = computeHealth(r).score;

                                return (
                                    <g key={r.id} className="mapDot" onClick={() => doRobotClick(r)} style={{ cursor: "pointer" }}>
                                        {/* Organic risk indicator */}
                                        {isOrg && (
                                            <circle cx={r.x} cy={r.y} r={7.5} fill="none" stroke={T.pu}
                                                strokeWidth="0.35" strokeDasharray="1,1" opacity={0.5} />
                                        )}
                                        {/* Multi-select ring */}
                                        {isM && <circle cx={r.x} cy={r.y} r={5.8} fill={`${T.cy}22`} stroke={T.cy} strokeWidth="0.65" opacity={0.9} />}
                                        {/* Selected ring */}
                                        {isSel && !isM && (
                                            <>
                                                <circle cx={r.x} cy={r.y} r={6} fill={`${T.a}12`} stroke={T.a} strokeWidth="0.7" opacity={0.95} />
                                                <circle cx={r.x} cy={r.y} r={8} fill="none" stroke={T.a} strokeWidth="0.3" opacity={0.3} />
                                            </>
                                        )}
                                        {/* Error pulse */}
                                        {isErr && (
                                            <circle cx={r.x} cy={r.y} r={5} fill="none" stroke={T.rd}
                                                strokeWidth="0.35" opacity={flash ? 0.8 : 0.1} />
                                        )}
                                        {/* Low health ring */}
                                        {health < 50 && !isErr && (
                                            <circle cx={r.x} cy={r.y} r={4.5} fill="none" stroke={T.am}
                                                strokeWidth="0.3" opacity={0.5} strokeDasharray="1,0.8" />
                                        )}
                                        {/* Robot body */}
                                        <circle cx={r.x} cy={r.y} r={st.dot}
                                            fill={isErr && flash ? "#ffffff" : st.color}
                                            opacity={op}
                                            style={{ filter: `drop-shadow(0 0 2px ${st.color}90)` }} />
                                        {/* Low battery amber indicator */}
                                        {(r.cb < 20 || r.mb < 20) && (
                                            <circle cx={r.x + 2.8} cy={r.y - 2.8} r={1.1} fill={T.am} opacity={op}
                                                style={{ filter: `drop-shadow(0 0 3px ${T.am})` }} />
                                        )}
                                    </g>
                                );
                            })}

                            {/* Selected robot callout */}
                            {robot && (() => {
                                const r = robots.find(x => x.id === sel);
                                if (!r) return null;
                                const h = computeHealth(r);
                                const hc = h.score >= 75 ? T.gr : h.score >= 50 ? T.am : T.rd;
                                return (
                                    <g>
                                        <line x1={r.x} y1={r.y} x2={r.x > 70 ? r.x - 5 : r.x + 5} y2={r.y - 6}
                                            stroke={T.a} strokeWidth="0.3" opacity={0.6} />
                                        <rect x={r.x > 70 ? r.x - 23 : r.x + 3} y={r.y - 11} width={20} height={11} rx="1"
                                            fill={T.bg2} stroke={T.a} strokeWidth="0.4" opacity={0.97} />
                                        <text x={r.x > 70 ? r.x - 22 : r.x + 4} y={r.y - 6.5}
                                            style={{ fill: T.a, fontSize: "2.6px", fontFamily: "monospace", fontWeight: "bold" }}>{r.fid}</text>
                                        <text x={r.x > 70 ? r.x - 22 : r.x + 4} y={r.y - 3.5}
                                            style={{ fill: T.txD, fontSize: "2px", fontFamily: "monospace" }}>{STATES[r.state]?.label}</text>
                                        <text x={r.x > 70 ? r.x - 22 : r.x + 4} y={r.y - 1}
                                            style={{ fill: hc, fontSize: "2px", fontFamily: "monospace" }}>HLTH {h.score}</text>
                                    </g>
                                );
                            })()}

                            {/* Compass rose */}
                            <g transform="translate(95.5,94.5)">
                                <circle r="4" fill={T.bg2} stroke={T.br} strokeWidth="0.5" />
                                <text textAnchor="middle" y="-1.2" style={{ fill: T.a, fontSize: "2px", fontFamily: "monospace", fontWeight: "bold" }}>N</text>
                                <text textAnchor="middle" y="3.2" style={{ fill: T.txD, fontSize: "1.6px", fontFamily: "monospace" }}>S</text>
                                <text x="-2.5" y="0.8" textAnchor="middle" style={{ fill: T.txD, fontSize: "1.6px", fontFamily: "monospace" }}>W</text>
                                <text x="2.5" y="0.8" textAnchor="middle" style={{ fill: T.txD, fontSize: "1.6px", fontFamily: "monospace" }}>E</text>
                            </g>

                            {/* Scale bar */}
                            <line x1="3" y1="97.5" x2="23" y2="97.5" stroke={T.txD} strokeWidth="0.35" />
                            <line x1="3" y1="96.5" x2="3" y2="98.5" stroke={T.txD} strokeWidth="0.35" />
                            <line x1="23" y1="96.5" x2="23" y2="98.5" stroke={T.txD} strokeWidth="0.35" />
                            <text x="13" y="96" textAnchor="middle" style={{ fill: T.txD, fontSize: "1.8px", fontFamily: "monospace" }}>500m</text>

                            {/* LIVE indicator */}
                            <circle cx="97" cy="3.5" r="1.2" fill={flash ? T.a : "transparent"} stroke={T.a} strokeWidth="0.4"
                                style={{ filter: flash ? `drop-shadow(0 0 3px ${T.a})` : undefined }} />
                            <text x="94" y="4" textAnchor="end" style={{ fill: T.a, fontSize: "1.8px", fontFamily: "monospace", letterSpacing: "0.1em" }}>LIVE</text>

                        </svg>
                    </div>
                </div>

                {/* ═══ RIGHT PANEL: Robot Detail + Fleet Overview ════════════ */}
                <div style={{
                    width: 290, display: "flex", flexDirection: "column", borderLeft: `1px solid ${T.br}`,
                    overflow: "hidden", background: T.bg1, flexShrink: 0
                }}>

                    {!robot ? (
                        /* Fleet overview */
                        <div style={{ flex: 1, overflowY: "auto", padding: "12px 13px" }}>
                            <SectionLabel text="Fleet Health Distribution" />
                            {Object.entries(STATES).map(([k, v]) => {
                                const cnt = robots.filter(r => r.state === k).length;
                                return (
                                    <div key={k} style={{ marginBottom: 8 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, alignItems: "center" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <div style={{ width: 7, height: 7, borderRadius: "50%", background: v.color }} />
                                                <span style={{ color: T.txL, fontSize: 10 }}>{v.label}</span>
                                            </div>
                                            <span style={{ color: v.color, fontSize: 12, fontWeight: 700 }}>{cnt}</span>
                                        </div>
                                        <div style={{ height: 4, background: T.bg4, borderRadius: 2, overflow: "hidden" }}>
                                            <div style={{
                                                width: `${cnt}%`, height: "100%", background: v.color, opacity: 0.75,
                                                boxShadow: `0 0 6px ${v.color}50`, transition: "width 0.8s"
                                            }} />
                                        </div>
                                    </div>
                                );
                            })}

                            <SectionLabel text="System Health Score" />
                            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12, padding: "10px", background: T.bg3, borderRadius: 6, border: `1px solid ${T.br}` }}>
                                <HealthRing score={stats.avgHealth} size={60} />
                                <div>
                                    <div style={{ color: T.txD, fontSize: 9 }}>Fleet Average</div>
                                    <div style={{ color: stats.avgHealth >= 75 ? T.gr : stats.avgHealth >= 50 ? T.am : T.rd, fontSize: 24, fontWeight: 700 }}>{stats.avgHealth}</div>
                                    <div style={{ color: T.txD, fontSize: 8 }}>/ 100 composite</div>
                                </div>
                            </div>

                            <SectionLabel text="Predictive Alerts" />
                            {robots.filter(r => {
                                const p = predictBattery(r);
                                return p.criticalIn < 20 && r.state !== "charging";
                            }).slice(0, 5).map(r => {
                                const p = predictBattery(r);
                                return (
                                    <div key={r.id} onClick={() => setSel(r.id)} style={{
                                        display: "flex", alignItems: "center", gap: 8, padding: "5px 7px", marginBottom: 3,
                                        background: T.bg3, borderRadius: 4, border: `1px solid ${T.am}30`, cursor: "pointer"
                                    }}
                                        onMouseEnter={e => e.currentTarget.style.borderColor = T.am}
                                        onMouseLeave={e => e.currentTarget.style.borderColor = `${T.am}30`}>
                                        <div style={{ color: T.am, fontSize: 9 }}>⚠</div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ color: T.tx, fontSize: 10 }}>{r.fid}</div>
                                            <div style={{ color: T.am, fontSize: 8 }}>Battery critical in ~{p.criticalIn}min</div>
                                        </div>
                                        <div style={{ color: T.txD, fontSize: 8 }}>→ RTB</div>
                                    </div>
                                );
                            })}

                            <SectionLabel text="Connectivity" />
                            <DataRow label="MQTT Broker" val="ONLINE" color={T.a} />
                            <DataRow label="Connected" val={`${stats.connected}/100`} color={T.a} />
                            <DataRow label="Avg Signal" val="3.2 / 4" color={T.cy} />
                            <DataRow label="Latency" val="48ms" />
                            <DataRow label="Packet Loss" val="0.2%" />
                            <DataRow label="Uptime" val="14d 6h 22m" color={T.a} />
                        </div>
                    ) : (
                        /* Selected robot detail */
                        <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

                            {/* Robot header */}
                            <div style={{ padding: "11px 13px", borderBottom: `1px solid ${T.br}`, background: T.bg2, flexShrink: 0 }}>
                                {(() => {
                                    const st = STATES[robot.state] || STATES.idle;
                                    const h = computeHealth(robot);
                                    const age = now - robot.upd;
                                    const ageStr = age < 5000 ? "Just now" : `${Math.floor(age / 1000)}s ago`;
                                    const pred = predictBattery(robot);
                                    return (
                                        <>
                                            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 9 }}>
                                                <HealthRing score={h.score} size={52} />
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ color: T.tx, fontSize: 16, fontWeight: 700, lineHeight: 1 }}>{robot.fid}</div>
                                                    <div style={{
                                                        color: st.color, fontSize: 10, marginTop: 3, letterSpacing: "0.05em",
                                                        animation: robot.state === "error" ? "blink 1s infinite" : undefined
                                                    }}>
                                                        {robot.state === "error" && flash ? "⚠ " : ""}{st.label.toUpperCase()}
                                                    </div>
                                                    {robot.organicRisk && (
                                                        <div style={{ color: T.pu, fontSize: 8, marginTop: 2, animation: "blink 1s infinite" }}>⬡ ORGANIC BOUNDARY RISK</div>
                                                    )}
                                                </div>
                                                <div style={{ textAlign: "right" }}>
                                                    <SigBars sig={robot.sig} />
                                                    <div style={{ color: T.txD, fontSize: 8, marginTop: 4 }}>{ageStr}</div>
                                                </div>
                                            </div>

                                            <div style={{ background: T.bg3, borderRadius: 5, padding: "7px 9px", marginBottom: 8 }}>
                                                <div style={{ color: T.txD, fontSize: 8.5, letterSpacing: "0.08em" }}>ACTIVE MISSION</div>
                                                <div style={{ color: T.cy, fontSize: 11, marginTop: 3, fontWeight: 600 }}>{robot.mission}</div>
                                                <div style={{ color: T.txD, fontSize: 8.5, marginTop: 2 }}>Zone {robot.zone} · {robot.fw} · Up {robot.uptime}h</div>
                                            </div>

                                            {pred.criticalIn < 25 && robot.state !== "charging" && (
                                                <div style={{ background: `${T.am}12`, border: `1px solid ${T.am}50`, borderRadius: 4, padding: "5px 8px" }}>
                                                    <div style={{ color: T.am, fontSize: 9.5, fontWeight: 700 }}>
                                                        ⚡ PREDICTIVE: Battery critical in ~{pred.criticalIn}min
                                                    </div>
                                                    <div style={{ color: T.txD, fontSize: 8.5, marginTop: 1 }}>
                                                        CB drain: {pred.drainRate.toFixed(2)}%/min — Recommend RTB now
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>

                            {/* Right tabs */}
                            <div style={{ display: "flex", borderBottom: `1px solid ${T.br}`, flexShrink: 0 }}>
                                {["telemetry", "health", "commands"].map(t => (
                                    <button key={t} onClick={() => setRightTab(t)} style={{
                                        flex: 1, background: rightTab === t ? T.bg3 : "transparent", border: "none",
                                        borderBottom: `2px solid ${rightTab === t ? T.a : "transparent"}`,
                                        color: rightTab === t ? T.a : T.txD, padding: "6px 0",
                                        cursor: "pointer", fontSize: 9, textTransform: "uppercase",
                                        letterSpacing: "0.06em", transition: "all 0.15s",
                                    }}>{t}</button>
                                ))}
                            </div>

                            {/* Tab bodies */}
                            <div style={{ flex: 1, overflowY: "auto", padding: "11px 13px" }}>

                                {rightTab === "telemetry" && (() => {
                                    const pred = predictBattery(robot);
                                    return (
                                        <>
                                            <Sec label="POWER SYSTEMS">
                                                <BatBar pct={robot.cb} color={T.a} label="Controller Battery (CB)" predictMin={pred.minsTo15CB < 30 ? pred.minsTo15CB : null} />
                                                <BatBar pct={robot.mb} color={T.cy} label="Motor Battery (MB)" predictMin={pred.minsTo15MB < 30 ? pred.minsTo15MB : null} />
                                                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                                                    <span style={{ color: T.txD, fontSize: 9 }}>CB Trend (20 ticks)</span>
                                                    <MiniSparkline data={robot.cbHistory} color={robot.cb < 25 ? T.rd : T.a} />
                                                </div>
                                            </Sec>

                                            <Sec label="MOTION">
                                                <DataRow label="Speed (Spd)" val={`${(robot.spd).toFixed(1)} km/h`} />
                                                <DataRow label="Direction (D)" val={robot.dir} />
                                                <DataRow label="Ground Clearance (GC)" val={`${robot.gc.toFixed(1)} cm`} color={robot.gc < 3 ? T.rd : robot.gc < 5 ? T.am : undefined} />
                                                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                                                    <span style={{ color: T.txD, fontSize: 9 }}>Speed Trend</span>
                                                    <MiniSparkline data={robot.speedHistory} color={T.cy} />
                                                </div>
                                            </Sec>

                                            <Sec label="ENVIRONMENT">
                                                <DataRow label="Soil pH (SP)" val={robot.sp.toFixed(1)} color={(robot.sp < 5.5 || robot.sp > 8.0) ? T.am : undefined}
                                                    sub={robot.sp < 5.5 ? "Below optimal" : robot.sp > 8.0 ? "Above optimal" : "Optimal range"} />
                                                <DataRow label="Zone" val={`Zone ${robot.zone}`} />
                                                <DataRow label="Organic Risk" val={robot.organicRisk ? "YES" : "NO"} color={robot.organicRisk ? T.pu : T.a} />
                                                <DataRow label="MQTT Signal" val={`${robot.sig} / 4`} color={robot.sig < 2 ? T.rd : robot.sig < 3 ? T.am : undefined} />
                                                <DataRow label="Last Update" val={`${Math.floor((now - robot.upd) / 1000)}s ago`} color={(now - robot.upd) > 20000 ? T.am : undefined} />
                                            </Sec>

                                            <Sec label="SYSTEM">
                                                <DataRow label="Firmware" val={robot.fw} />
                                                <DataRow label="Node ID" val={`FID-${robot.id}`} />
                                                <DataRow label="Uptime" val={`${robot.uptime}h`} />
                                                <DataRow label="Fault Count" val={robot.errors} color={robot.errors > 1 ? T.rd : robot.errors > 0 ? T.am : undefined} />
                                            </Sec>
                                        </>
                                    );
                                })()}

                                {rightTab === "health" && (() => {
                                    const h = computeHealth(robot);
                                    const dims = [
                                        { label: "Power Health", val: h.power, color: h.power < 30 ? T.rd : h.power < 60 ? T.am : T.gr, desc: "CB×0.4 + MB×0.6" },
                                        { label: "Mechanical Health", val: h.mech, color: h.mech < 50 ? T.rd : h.mech < 80 ? T.am : T.gr, desc: "Ground clearance" },
                                        { label: "Connectivity", val: h.conn, color: h.conn < 50 ? T.rd : h.conn < 75 ? T.am : T.gr, desc: "Signal strength" },
                                        { label: "Soil Compliance", val: h.phScore, color: h.phScore < 50 ? T.rd : h.phScore < 80 ? T.am : T.gr, desc: "Soil pH range" },
                                        { label: "Mission Quality", val: h.mission, color: h.mission < 50 ? T.rd : h.mission < 80 ? T.am : T.gr, desc: "Fault ratio" },
                                    ];
                                    return (
                                        <>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                                                <HealthRing score={h.score} size={80} />
                                            </div>
                                            <SectionLabel text="Health Dimensions" />
                                            {dims.map(d => (
                                                <div key={d.label} style={{ marginBottom: 9 }}>
                                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                                                        <div>
                                                            <span style={{ color: T.txL, fontSize: 10 }}>{d.label}</span>
                                                            <div style={{ color: T.txD, fontSize: 8 }}>{d.desc}</div>
                                                        </div>
                                                        <span style={{ color: d.color, fontWeight: 700, fontSize: 12 }}>{d.val}</span>
                                                    </div>
                                                    <div style={{ height: 4, background: T.bg4, borderRadius: 2, overflow: "hidden" }}>
                                                        <div style={{
                                                            width: `${d.val}%`, height: "100%", background: d.color,
                                                            transition: "width 1.2s", boxShadow: `0 0 6px ${d.color}50`
                                                        }} />
                                                    </div>
                                                </div>
                                            ))}
                                            <SectionLabel text="Recommendation" />
                                            <div style={{ background: T.bg3, borderRadius: 4, padding: "8px 9px", fontSize: 10, color: T.txL, lineHeight: 1.6 }}>
                                                {h.score >= 80 ? `✓ Robot operating at peak — no action required.` :
                                                    h.score >= 60 ? `⚠ Monitor battery levels. Schedule maintenance.` :
                                                        h.score >= 40 ? `⚠ Return to base recommended for service check.` :
                                                            `✕ Critical — return to base immediately and run diagnostics.`}
                                            </div>
                                        </>
                                    );
                                })()}

                                {rightTab === "commands" && (
                                    <>
                                        <SectionLabel text="QUICK COMMANDS" />
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5, marginBottom: 14 }}>
                                            {[
                                                ["Return Base", T.a], ["Pause", T.am],
                                                ["Resume", T.gr], ["Diagnostics", T.cy],
                                                ["Start Sowing", T.gr], ["Start Spray", T.cy],
                                                ["Start Cutting", T.ye], ["Emergency Stop", T.rd],
                                            ].map(([label, color]) => (
                                                <button key={label} style={{
                                                    background: T.bg3, border: `1px solid ${T.brX}`,
                                                    color: T.txD, borderRadius: 4, padding: "8px 4px",
                                                    cursor: "pointer", fontSize: 9.5, transition: "all 0.15s",
                                                    textAlign: "center",
                                                }}
                                                    onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.color = color; e.currentTarget.style.background = `${color}12`; }}
                                                    onMouseLeave={e => { e.currentTarget.style.borderColor = T.brX; e.currentTarget.style.color = T.txD; e.currentTarget.style.background = T.bg3; }}>
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                        <SectionLabel text="MQTT COMMAND LOG" />
                                        <div style={{ background: T.bg0, borderRadius: 4, padding: "8px", fontFamily: "monospace", fontSize: 9.5, color: T.txD, lineHeight: 1.8 }}>
                                            <div style={{ color: T.a }}>→ {"{"}"FID":"{robot.fid}","Cmd":"STATUS"{"}"}</div>
                                            <div style={{ color: T.cy }}>← {"{"}"state":"{robot.state}","cb":{Math.round(robot.cb)}{"}"}</div>
                                            <div style={{ color: T.txD }}>topic: robot/{robot.fid}/cmd</div>
                                            <div style={{ color: T.txD }}>qos: 1 | retain: false</div>
                                        </div>
                                        <div style={{ marginTop: 10 }}>
                                            <button onClick={() => setSel(null)} style={{
                                                background: "transparent", border: `1px solid ${T.brX}`, color: T.txD,
                                                borderRadius: 4, padding: "6px 0", cursor: "pointer", fontSize: 10,
                                                width: "100%", transition: "all 0.15s",
                                            }}
                                                onMouseEnter={e => { e.currentTarget.style.borderColor = T.txD; e.currentTarget.style.color = T.tx; }}
                                                onMouseLeave={e => { e.currentTarget.style.borderColor = T.brX; e.currentTarget.style.color = T.txD; }}>
                                                ← Deselect Robot
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
    
}

export default AgriCommandCenter;
